<script setup lang="ts">
import { ButtonType, CustomButton } from '@/components/common';
import type { EntryModel, FormModel } from '@/models';
import { BACKEND_URL } from '@/services/apiClient';
import { useStudyStore } from '@/stores/study';
import { useEntryStore } from '@/stores/entry';
import { useFormStore } from '@/stores/forms';
import { DATE_FORMAT_2, formattedDate, routeConstants } from '@/utils';
import Select from 'primevue/select';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const formsStore = useFormStore();
const entryStore = useEntryStore();
const studyStore = useStudyStore();

const route = useRoute();
const router = useRouter();
const selectedForm = ref<FormModel>();
const selectedEntry = ref<EntryModel>();

onMounted(() => {
  // Initialize the selected form based on the current route parameter
  const formIdentifier = route.params.formIdentifier as string;
  selectedForm.value = formsStore.formList.find(
    form => form.identifier === formIdentifier
  );
  if (selectedForm.value) {
    const selectedFormEntries =
      entryStore.form?.selectedCase?.entries?.[selectedForm.value!.identifier];

    const entryIdentifier = route.params.entryIdentifier as string;
    if (entryIdentifier) {
      selectedEntry.value = selectedFormEntries?.find(
        entry => entry.identifier === entryIdentifier
      );
    }

    const newEntryExists = selectedFormEntries?.some(
      entry => entry.formTitle === 'New entry'
    );
    if (!selectedForm.value?.enforcements.onlyOnce && !newEntryExists) {
      selectedFormEntries?.push({
        identifier: '',
        formTitle: 'New entry',
        items: {},
        createdAt: new Date(),
        creatorAvatar: '',
        creatorNames: ''
      } as EntryModel);
    }
  }
});

const disablePreviousButton = computed(() => {
  const currentFormIndex = formsStore.formList.findIndex(
    form => form.identifier === route.params.formIdentifier
  );
  return currentFormIndex <= 0;
});
const disableNextButton = computed(() => {
  const currentFormIndex = formsStore.formList.findIndex(
    form => form.identifier === route.params.formIdentifier
  );
  return currentFormIndex >= formsStore.formList.length - 1;
});

const handleNavigation = (direction: 'previous' | 'next') => {
  const currentFormIndex = formsStore.formList.findIndex(
    form => form.identifier === route.params.formIdentifier
  );

  if (currentFormIndex > -1) {
    let formId = route.params.formIdentifier as string;
    if (direction === 'previous' && currentFormIndex > 0) {
      // Navigate to the previous form
      const previousForm = formsStore.formList[currentFormIndex - 1];
      formId = previousForm.identifier;
    } else if (
      direction === 'next' &&
      currentFormIndex < formsStore.formList.length - 1
    ) {
      // Navigate to the next form
      const nextForm = formsStore.formList[currentFormIndex + 1];
      formId = nextForm.identifier;
    }

    if (formId !== route.params.formIdentifier) {
      navigateToForm(formId);
    }
  }
};

const navigateToForm = (formId: string) => {
  const entries = entryStore.form?.selectedCase?.entries;
  if (entries && Object.keys(entries).length > 0 && entries[formId]) {
    const entry = entries[formId][0];
    router.replace({
      name: routeConstants.CASE_ENTRY.name,
      params: {
        identifier: route.params.identifier as string,
        formIdentifier: formId,
        entryIdentifier: entry.identifier
      }
    });
  } else {
    router.replace({
      name: routeConstants.CASE_NEW_ENTRY.name,
      params: {
        identifier: route.params.identifier as string,
        formIdentifier: formId
      }
    });
  }
  selectedForm.value = formsStore.formList.find(
    form => form.identifier === formId
  );
};

const handleFormSelection = () => {
  if (selectedForm.value) {
    navigateToForm(selectedForm.value.identifier);
  }
};

const handleEntrySelection = () => {
  if (selectedEntry.value && selectedEntry.value.formTitle !== 'New entry') {
    router.replace({
      name: routeConstants.CASE_ENTRY.name,
      params: {
        identifier: route.params.identifier as string,
        formIdentifier: selectedForm.value?.identifier,
        entryIdentifier: selectedEntry.value.identifier
      }
    });
  } else if (selectedEntry.value && selectedEntry.value.formTitle === 'New entry') {
    router.replace({
      name: routeConstants.CASE_NEW_ENTRY.name,
      params: {
        identifier: route.params.identifier as string,
        formIdentifier: selectedForm.value?.identifier
      }
    });
  }
};

const formEntries = computed(() => {
  return entryStore.form?.selectedCase?.entries && selectedForm.value ?
    entryStore.form?.selectedCase?.entries![selectedForm.value?.identifier] :
    [];
});

const showEntryItemFieldAnswerInTitle = computed(() => {
  const dataObjectKey = studyStore.selectedStudy?.flags?.customDataDictionary ?
    `${studyStore.selectedStudy?.flags?.customDataDictionary}_DATA` : undefined;
  //@ts-expect-error: dynamic data for PUMP
  return dataObjectKey && studyStore.selectedStudy?.flags[dataObjectKey] ?
    //@ts-expect-error: dynamic data for PUMP
    studyStore.selectedStudy?.flags[dataObjectKey][
      selectedForm.value?.identifier
    ] :
    false;
});
</script>

<template>
  <div class="entry-navigation-root">
    <div class="previous-button">
      <CustomButton :type="ButtonType.ICON_ONLY"
        @onClick="() => handleNavigation('previous')"
        :icon="{ icon: 'left', style: 'far' }"
        :disabled="disablePreviousButton"/>
    </div>
    <div class="p-dropdown-wrapper">
      <Select v-model="selectedForm"
        placeholder="Select a form"
        @change="handleFormSelection"
        :options="formsStore.formList">
        <template #value="slotProps">
          <div v-if="slotProps.value" class="type-item">
            <div class="icon">
              <FontAwesomeIcon :icon="[`fas`, `file`]" />
            </div>
            <div>{{ slotProps.value.title }}</div>
          </div>
          <span v-else>
            {{ slotProps.placeholder }}
          </span>
        </template>
        <template #option="slotProps">
          <div class="type-item">
            <div class="icon">
              <FontAwesomeIcon :icon="[`far`, `file`]" />
            </div>
            <div class="label">{{ slotProps.option.title }}</div>
          </div>
        </template>
      </Select>
    </div>
    <div class="p-dropdown-wrapper" v-if="!selectedForm?.enforcements.onlyOnce">
      <Select v-model="selectedEntry"
        placeholder="Select an entry"
        @change="handleEntrySelection"
        :options="formEntries">
        <template #value="slotProps">
          <div v-if="slotProps.value" class="type-item">
            <div class="icon">
              <FontAwesomeIcon :icon="[`fas`, `file`]" />
            </div>
            <div>
              {{ slotProps.value.formTitle }}
              {{
                showEntryItemFieldAnswerInTitle
                  ? `(${
                      slotProps.value.items
                        ? slotProps.value.items[showEntryItemFieldAnswerInTitle]
                        : ""
                    })`
                  : ""
              }}
            </div>
          </div>
        </template>
        <template #option="slotProps">
          <FontAwesomeIcon :icon="['far', slotProps.option.formTitle != 'New entry' ? `file` : `file-circle-plus`]" />
          <div class="entry-item-label">
            {{ slotProps.option.formTitle }}
            {{
              showEntryItemFieldAnswerInTitle
                ? slotProps.option.items[showEntryItemFieldAnswerInTitle]
                  ? `(${slotProps.option.items[showEntryItemFieldAnswerInTitle]}),`
                  : ""
                : ""
            }}
          </div>
          <div class="entry-details"
            v-if="slotProps.option.formTitle != 'New entry'">
            <div class="entry-item-label">
              {{ formattedDate(slotProps.option.createdAt, DATE_FORMAT_2) }}
            </div>
            <img :src="`${BACKEND_URL}${slotProps.option.creatorAvatar}`"
              class="avatar"
              v-if="slotProps.option.creatorAvatar"/>
            <div class="entry-item-creator">
              {{ slotProps.option.creatorNames }}
            </div>
          </div>
        </template>
      </Select>
    </div>
    <div class="previous-button">
      <CustomButton :type="ButtonType.ICON_ONLY"
        @onClick="() => handleNavigation('next')"
        :icon="{ icon: 'right', style: 'far' }"
        :disabled="disableNextButton"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../../assets/variables.module";
.entry-navigation-root {
  width: 1000px;
  display: flex;
  margin-bottom: 16px;
  border: solid 1px $disabled;
  border-radius: 4px;
  padding: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  justify-content: space-between;
}
.previous-button {
  border: solid 1px $disabled;
  border-radius: 4px;
}
.type-item {
  display: flex;
  align-items: center;

  .icon {
    margin-right: 8px;
    font-size: 16px;
  }
}
:deep(.p-select-label) {
  padding: 0.25rem 0.75rem;
}

.entry-item-label {
  margin-left: 12px;
  line-height: 16px;
}

.entry-details {
  display: flex;
}

.entry-item-creator {
  margin-left: 8px;
}

.avatar {
  width: 24px;
  margin-left: 16px;
  border-radius: 50%;
}
</style>
